import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Input, Spinner, Label, Select } from '@iq/react-components';

import { getActiveSite } from '../../../bundles/sites';
import { getTags, updateTag, deleteTags, requestTags } from '../../../bundles/tags';
import { displayNotification } from '../../../bundles/notifications';
import services from '../../../services';
import { TAG_CATEGORIES } from '../../../constants';

import ConfirmationDialog from '../../ConfirmationDialog';
import TagSelect from '../../TagSelect';
import Heading from '../../Heading';

const TagsView = () => {
  const dispatch = useDispatch();

  const site = useSelector(getActiveSite);
  const tags = useSelector(getTags);

  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(TAG_CATEGORIES[0]);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [isEditable, setIsEditable] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tagName, setTagName] = useState('');
  const [checkingTags, setCheckingTags] = useState(false);

  useEffect(() => {
    if (site.id) {
      dispatch(requestTags(site.id));
    }
  }, [site.id]);

  useEffect(() => {
    if (selectedTagIds.length === 1) {
      setIsEditable(true);
    } else {
      setIsEditable(false);
    }
  }, [selectedTagIds]);

  useEffect(() => setIsEditing(false), [tags]);

  useEffect(() => {
    if (isEditing) {
      setTagName(() => (tags.find((t) => t.id === selectedTagIds[0]) || {}).name || '');
    }
  }, [isEditing]);

  const onCategorySelect = useCallback(
    (option) => {
      setSelectedCategory(() => option.value);
    },
    [setSelectedCategory]
  );

  useEffect(() => {
    setSelectedTagIds([]);
  }, [selectedCategory]);

  const onDeleteTags = useCallback(async () => {
    await setCheckingTags(true);
    const checkedTags = await Promise.all(
      selectedTagIds.map(async (tagId) => {
        let connectedEntities = 0;
        try {
          if (selectedCategory === 'events') {
            const { total } = await services.event.getEvents({
              site: site.id,
              org: site.org,
              tags: [tagId],
            });
            connectedEntities = total;
          }
          if (selectedCategory === 'files') {
            const { total } = await services.file.getFiles({
              site: site.id,
              org: site.org,
              tags: [
                {
                  filterType: 'include',
                  operator: 'or',
                  tags: [tagId],
                },
              ],
            });
            connectedEntities = total;
          }
          return {
            ...(tags.find((t) => t.id === tagId) || {}),
            connectedEntities,
          };
        } catch (error) {
          return { error };
        }
      })
    );
    if (checkedTags.some((t) => t.error)) {
      dispatch(
        displayNotification({
          title: 'Unable to delete tags',
          message: 'Encountered error when checking for entities connected to tags.',
          type: 'persistent',
          status: 'warning',
          id: 'delete-tag',
        })
      );
    } else {
      await setTagsToDelete(() => checkedTags.filter((t) => t.id));
    }
    setCheckingTags(false);
  }, [selectedTagIds, tags, site, selectedCategory]);

  const confirmDeleteBody = useMemo(() => {
    if (checkingTags) {
      return (
        <div className="checking-tags">
          <Spinner size="s" />
          <span>Finding tag associations...</span>
        </div>
      );
    }
    const plural = tagsToDelete.length > 1;

    return (
      <>
        <p>{`Are you sure you want to delete the following tag${plural ? 's' : ''}?`}</p>
        <div className="tag-container">
          {tagsToDelete.map((t) => (
            <div
              key={t.id}
              className="tag-connections"
            >
              <span>{t.name}</span>
              {t.connectedEntities > 0 && (
                <span>
                  {`Used in ${t.connectedEntities} ${t.connectedEntities > 1 ? selectedCategory : selectedCategory.slice(0, -1)}`}
                </span>
              )}
            </div>
          ))}
        </div>
      </>
    );
  }, [tagsToDelete, checkingTags, selectedCategory]);

  const onConfirmDeleteTags = useCallback(() => {
    dispatch(
      deleteTags(
        site.id,
        tagsToDelete.map((t) => t.id),
        selectedCategory
      )
    );
    setTagsToDelete([]);
    setSelectedTagIds([]);
  }, [tagsToDelete, site.id, selectedCategory]);

  const confirmUpdateBody = useMemo(() => {
    const editTag = tags.find((t) => t.id === selectedTagIds[0]);
    return editTag ? (
      <div className="tag-edit-modal">
        {editTag.categories?.length > 1 && (
          <>
            <p>{`This tag belongs to the following categories: ${editTag.categories.join(', ')}.`}</p>
            <p>{`Changes will be reflected in this tag across all categories.`}</p>
          </>
        )}
        <div className="tag-edit-modal__input">
          <Label htmlFor="tag-name">Tag name</Label>
          <Input
            id="tag-name"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            required
          />
        </div>
      </div>
    ) : null;
  }, [tags, selectedTagIds, tagName, setTagName]);

  const onUpdateTag = () => {
    const editTag = tags.find((t) => t.id === selectedTagIds[0]);
    if (editTag) {
      const tagUpdate = { ...editTag, name: tagName };
      // remove color, icon as not being used
      delete tagUpdate.color;
      delete tagUpdate.icon;
      dispatch(updateTag(tagUpdate));
    }
  };

  const categoryOptions = TAG_CATEGORIES.map((cat) => ({
    value: cat,
    label: cat.charAt(0).toUpperCase() + cat.slice(1),
  }));
  const categoryValue = categoryOptions.find((opt) => opt.value === selectedCategory);

  return (
    <div className="tags-view">
      <Heading contentLeft={<div className="title">Create, edit or delete tags</div>} />
      <div className="tags-view__row">
        <div className="tags-view__select-container">
          <Label htmlFor="category-select">Category</Label>
          <Select
            id="category-select"
            label="Category"
            isMulti={false}
            onChange={onCategorySelect}
            creatable={false}
            value={categoryValue}
            options={categoryOptions}
            closeMenuOnSelect
            placeholder="Select category"
          />
        </div>
      </div>
      <div className="tags-view__row">
        <div className="tags-view__select-container">
          <Label htmlFor="tag-select">{'Tag(s)'}</Label>
          <TagSelect
            id="tag-select"
            category={selectedCategory}
            value={selectedTagIds}
            onChange={setSelectedTagIds}
            placeholder="Create or select tags"
            isDisabled={isEditing}
          />
        </div>
      </div>
      <div className="tags-view__tag-actions">
        <Button
          activity="danger"
          type="button"
          onClick={onDeleteTags}
          disabled={!selectedTagIds.length}
        >
          Delete
        </Button>
        <Button
          activity="primary"
          type="button"
          onClick={() => setIsEditing(true)}
          disabled={!isEditable}
        >
          Edit
        </Button>
      </div>

      {checkingTags || tagsToDelete.length ? (
        <ConfirmationDialog
          onCancel={() => setTagsToDelete([])}
          onConfirm={onConfirmDeleteTags}
          confirmType="danger"
          title="Delete Tags"
          body={confirmDeleteBody}
          confirmText="Delete"
          disabled={checkingTags}
          showCancel
        />
      ) : null}

      {isEditing ? (
        <ConfirmationDialog
          onCancel={() => setIsEditing(false)}
          onConfirm={onUpdateTag}
          confirmType="primary"
          title="Edit Tag"
          body={confirmUpdateBody}
          confirmText="Update"
          disabled={checkingTags}
          showCancel
        />
      ) : null}
    </div>
  );
};

export default TagsView;
